import { createEffect, createMemo, createSignal, onCleanup, onMount, Show } from "solid-js";
import { Outlet, useLocation, useNavigate, useRouterState } from "@tanstack/solid-router";
import type { AbstractPowerSyncDatabase } from "@powersync/common";

import { getPendingProposedCountQuery, queryKeys } from "@jornada/shared";
import { createPowerSyncQuery } from "@jornada/shared/solid";

import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { appState, Skeleton, useInteractionFlag } from "@fleet/ui";
import { useTheme } from "@/providers/theme-provider";
import { useCommandHandler } from "@fleet/shortcuts";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";

import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { ShortcutsModal } from "@/components/shortcuts-modal";
import { AccountSwitcherHost } from "@/features/auth/account-switcher-host";
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import type { SidebarStats } from "@/components/app-sidebar";
import { AppErrorBoundary } from "@/components/ui/error-boundary";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";

interface AppLayoutContentProps {
  db: AbstractPowerSyncDatabase | null;
}

/**
 * Granular skeleton for the main content region, shown while PowerSync's local
 * database is still initializing (`props.db` is null). Mirrors the coarse shape
 * of a typical list/detail screen so the shell never collapses to blank space.
 */
function ContentSkeleton() {
  return (
    <div class="flex flex-1 flex-col gap-6 p-4 sm:p-6" aria-hidden="true">
      {/* Page header */}
      <div class="flex items-center justify-between gap-4">
        <div class="flex flex-col gap-2">
          <Skeleton class="h-7 w-48" />
          <Skeleton class="h-4 w-64" />
        </div>
        <Skeleton class="h-9 w-28" />
      </div>

      {/* Summary cards */}
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Skeleton class="h-24 w-full" />
        <Skeleton class="h-24 w-full" />
        <Skeleton class="h-24 w-full" />
        <Skeleton class="h-24 w-full" />
      </div>

      {/* List rows */}
      <div class="flex flex-col gap-3">
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
      </div>
    </div>
  );
}

function useSidebarEscape(isSidebarOpen: () => boolean, setIsSidebarOpen: (o: boolean) => void) {
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };
    globalThis.addEventListener("keydown", handleEscapeForSidebar);
    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

export function AppLayoutContent(props: AppLayoutContentProps) {
  onMount(() => {
    // Notify the InitialSyncOverlay that we are mounted and ready
    appState.markAsReady();
  });

  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const routerState = useRouterState();
  const isNavigating = () => routerState().status === "pending";

  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;
  const theme = useTheme();

  const [isSidebarOpen, setIsSidebarOpen] = createSignal(false);
  const [shortcutsOpen, setShortcutsOpen] = createSignal(false);

  // Close the mobile drawer when navigating.
  createEffect(() => {
    void location().pathname;
    setIsSidebarOpen(false);
  });

  useSidebarEscape(isSidebarOpen, setIsSidebarOpen);

  // With the nav drawer open, the horizontal gesture belongs to it (drag to
  // dismiss). There is no page-level swipe gesture in this repo yet, but the
  // flag is the declaration of who owns horizontal dragging regardless — the
  // day one is added, it stands down for free.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  // Section key for the per-route entrance animation (slide-up + fade on mount).
  // Keying on the pathname remounts the wrapper around <Outlet> on navigation,
  // replaying the GPU-only `slide-in-up` keyframe (transform + opacity). The
  // inspection-run carousel pages section state internally (no URL change), so
  // paging through sections does NOT re-trigger the page entrance.
  // `prefers-reduced-motion` is honored globally in global.css.
  const routeSectionKey = createMemo(() => location().pathname || "/");

  // ── Global shortcuts (subset of the inherited system) ─────────────────────
  useCommandHandler(COMMAND_IDS.HELP_OPEN, () => {
    setShortcutsOpen(true);
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_SIDEBAR, () => {
    setIsSidebarOpen((prev) => !prev);
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BLUR, () => {
    void theme.toggleBlur();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME, () => {
    void theme.toggleFollowDevice();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BACKGROUND_STYLE, () => {
    theme.toggleBackgroundStyle();
  });
  useCommandHandler(COMMAND_IDS.GOTO_DASHBOARD, () => {
    void navigate({ to: "/" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_PROFILE, () => {
    void navigate({ to: "/profile" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_THEMES, () => {
    void navigate({ to: "/themes" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_SHORTCUTS, () => {
    void navigate({ to: "/profile/shortcuts" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_LANGUAGE, () => {
    void navigate({ to: "/language" });
  });

  // ── Sidebar badge: proposed entries pending review ─────────────────────
  const pendingReviews = createPowerSyncQuery<{ total: number }>({
    queryKey: () => queryKeys.workforce.pendingCount(userId()),
    db: () => props.db,
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getPendingProposedCountQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });

  const stats = (): SidebarStats | undefined => {
    if (pendingReviews.isLoading) {
      return undefined;
    }
    return { pendingReviews: pendingReviews.data?.[0]?.total ?? 0 };
  };

  return (
    <AppBackground
      class="flex h-dvh text-foreground overflow-hidden"
      isBlur={theme.isBlur()}
      patternType={theme.tokens()?.patternType}
      patternOpacity={theme.tokens()?.patternOpacity}
    >
      {/* Skip-to-content: first focusable element, visually hidden until focused. */}
      <a
        href="#main-content"
        class="sr-only rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-lg focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[200]"
      >
        {t(msg`Skip to content`)}
      </a>

      <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} stats={stats} />

      {/* ── Main content ───────────────────────────────────────── */}
      <main
        id="main-content"
        tabindex="-1"
        class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)] outline-none"
      >
        <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
        <OfflineBanner />
        <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
          <div class="flex flex-1 flex-col min-h-0 h-full">
            {/* Route content is data-bound: hold a granular skeleton until the
                local database is ready, then mount the real <Outlet />. A region
                ErrorBoundary keeps a thrown render error scoped to the content
                area — the shell (sidebar/header) stays interactive. */}
            <Show when={props.db} fallback={<ContentSkeleton />}>
              <AppErrorBoundary region={t(msg`this view`)}>
                {/* Per-route entrance: keying on the pathname remounts this
                    wrapper on navigation, replaying `slide-in-up` (GPU-only:
                    transform + opacity). Internal carousel/section state does
                    not change the key, so it won't re-trigger. Reduced-motion
                    is honored globally in global.css. */}
                <Show when={routeSectionKey()} keyed>
                  <div class="flex flex-1 flex-col min-h-0 h-full animate-slide-in-up will-change-[transform,opacity]">
                    <Outlet />
                  </div>
                </Show>
              </AppErrorBoundary>
            </Show>
          </div>
        </div>
      </main>

      <ShortcutsModal
        isOpen={shortcutsOpen()}
        onClose={() => {
          setShortcutsOpen(false);
        }}
      />

      {/* Multi-account switcher: mounted ONCE at layout level, URL-driven
          (`?account=switch`). The sidebar trigger only flips the URL flag. */}
      <AccountSwitcherHost />
    </AppBackground>
  );
}
